import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, CircularProgress, Alert,
  IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Add as AddIcon,
  ChevronLeft as PrevIcon, ChevronRight as NextIcon,
  CalendarMonth as CalendarIcon, Delete as DeleteIcon,
  Edit as EditIcon, Link as LinkIcon,
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay,
  startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval,
  isWithinInterval, parseISO, differenceInHours,
} from 'date-fns';
import resourceService, { Resource, ResourceBooking } from '../services/resourceService';

type ViewMode = 'week' | 'month';

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7:00 - 19:00

const statusColors: Record<string, string> = {
  confirmed: '#10B981',
  tentative: '#F59E0B',
  cancelled: '#EF4444',
};

const ResourceCalendarPage: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();

  const [resource, setResource] = useState<Resource | null>(null);
  const [bookings, setBookings] = useState<ResourceBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // Booking dialog
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    title: '', description: '', startTime: '', endTime: '',
    quantity: '', status: 'confirmed',
  });

  useEffect(() => {
    if (resourceId) loadData();
  }, [resourceId, currentDate, viewMode]);

  const loadData = async () => {
    if (!resourceId) return;
    try {
      setLoading(true);
      const rangeStart = viewMode === 'week'
        ? startOfWeek(currentDate, { weekStartsOn: 1 })
        : startOfMonth(currentDate);
      const rangeEnd = viewMode === 'week'
        ? endOfWeek(currentDate, { weekStartsOn: 1 })
        : endOfMonth(currentDate);

      const [res, bk] = await Promise.all([
        resourceService.getResource(resourceId),
        resourceService.getBookings(resourceId, rangeStart.toISOString(), rangeEnd.toISOString()),
      ]);
      setResource(res);
      setBookings(bk);
    } catch (err: any) {
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async () => {
    if (!resourceId) return;
    try {
      await resourceService.createBooking(resourceId, {
        title: bookingForm.title,
        description: bookingForm.description || undefined,
        startTime: new Date(bookingForm.startTime).toISOString(),
        endTime: new Date(bookingForm.endTime).toISOString(),
        quantity: bookingForm.quantity ? parseInt(bookingForm.quantity) : undefined,
        status: bookingForm.status,
      });
      setBookingDialog(false);
      setBookingForm({ title: '', description: '', startTime: '', endTime: '', quantity: '', status: 'confirmed' });
      loadData();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data || 'Failed to create booking';
      setError(typeof msg === 'string' ? msg : 'Booking conflict - resource already booked');
    }
  };

  const handleDeleteBooking = async (bookingId: string) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await resourceService.deleteBooking(bookingId);
      loadData();
    } catch (err) {
      setError('Failed to cancel booking');
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const openBookingDialog = (date?: Date) => {
    const start = date || new Date();
    const startStr = format(start, "yyyy-MM-dd'T'09:00");
    const endStr = format(start, "yyyy-MM-dd'T'10:00");
    setBookingForm({ ...bookingForm, title: '', description: '', startTime: startStr, endTime: endStr, quantity: '' });
    setBookingDialog(true);
  };

  const getDaysInView = () => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    return eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  };

  const getBookingsForDay = (day: Date) => {
    return bookings.filter(b => {
      const bStart = parseISO(b.startTime);
      const bEnd = parseISO(b.endTime);
      return isSameDay(day, bStart) || isSameDay(day, bEnd) ||
        isWithinInterval(day, { start: bStart, end: bEnd });
    });
  };

  if (loading && !resource) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/resources')}><BackIcon /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">{resource?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {resource?.resourceTypeName} | Capacity: {resource?.capacity} {resource?.capacityUnit}
              {resource?.location && ` | ${resource.location}`}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openBookingDialog()}>
          New Booking
        </Button>
      </Box>

      {/* Navigation */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton onClick={() => navigateDate('prev')}><PrevIcon /></IconButton>
          <Typography variant="h6">
            {viewMode === 'week'
              ? `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`
              : format(currentDate, 'MMMM yyyy')
            }
          </Typography>
          <IconButton onClick={() => navigateDate('next')}><NextIcon /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setCurrentDate(new Date())}>Today</Button>
          <Button variant={viewMode === 'week' ? 'contained' : 'outlined'} size="small"
            onClick={() => setViewMode('week')}>Week</Button>
          <Button variant={viewMode === 'month' ? 'contained' : 'outlined'} size="small"
            onClick={() => setViewMode('month')}>Month</Button>
        </Box>
      </Paper>

      {/* Calendar Grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {viewMode === 'week' ? (
          // Week View - day columns with time slots
          <Box sx={{ display: 'flex' }}>
            {getDaysInView().map(day => (
              <Box key={day.toISOString()} sx={{
                flex: 1, borderRight: '1px solid #e0e0e0', minWidth: 120,
                '&:last-child': { borderRight: 'none' },
              }}>
                <Box sx={{
                  p: 1, textAlign: 'center', borderBottom: '2px solid #e0e0e0',
                  bgcolor: isSameDay(day, new Date()) ? 'primary.50' : 'grey.50',
                }}>
                  <Typography variant="caption" display="block">{format(day, 'EEE')}</Typography>
                  <Typography variant="h6" color={isSameDay(day, new Date()) ? 'primary' : 'inherit'}>
                    {format(day, 'd')}
                  </Typography>
                </Box>
                <Box sx={{ minHeight: 300, p: 0.5, cursor: 'pointer' }}
                  onClick={() => openBookingDialog(day)}>
                  {getBookingsForDay(day).map(booking => {
                    const hours = differenceInHours(parseISO(booking.endTime), parseISO(booking.startTime));
                    return (
                      <Paper key={booking.id} elevation={1} sx={{
                        p: 0.75, mb: 0.5, cursor: 'pointer',
                        bgcolor: statusColors[booking.status] || '#3B82F6',
                        color: '#fff', borderRadius: 1, fontSize: '0.75rem',
                      }}>
                        <Typography variant="caption" fontWeight={600} display="block" noWrap>
                          {booking.title}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {format(parseISO(booking.startTime), 'HH:mm')} - {format(parseISO(booking.endTime), 'HH:mm')}
                        </Typography>
                        {booking.taskTitle && (
                          <Chip label={booking.taskTitle} size="small" sx={{
                            height: 16, fontSize: '0.6rem', mt: 0.5,
                            bgcolor: 'rgba(255,255,255,0.3)', color: '#fff',
                          }} />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <IconButton size="small" sx={{ color: '#fff', p: 0.25 }}
                            onClick={(e) => { e.stopPropagation(); handleDeleteBooking(booking.id); }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Paper>
                    );
                  })}
                  {getBookingsForDay(day).length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{
                      display: 'block', textAlign: 'center', mt: 4, opacity: 0.5,
                    }}>
                      Click to book
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          // Month View - grid
          <Box>
            <Grid container>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <Grid item xs={12 / 7} key={d} sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100', borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" fontWeight={600}>{d}</Typography>
                </Grid>
              ))}
            </Grid>
            <Grid container>
              {(() => {
                const monthStart = startOfMonth(currentDate);
                const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                const cells = Array.from({ length: 42 }, (_, i) => addDays(calendarStart, i));
                return cells.map(day => {
                  const dayBookings = getBookingsForDay(day);
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  return (
                    <Grid item xs={12 / 7} key={day.toISOString()} sx={{
                      minHeight: 80, borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                      p: 0.5, cursor: 'pointer', opacity: isCurrentMonth ? 1 : 0.3,
                      bgcolor: isSameDay(day, new Date()) ? 'primary.50' : 'inherit',
                    }} onClick={() => openBookingDialog(day)}>
                      <Typography variant="caption" fontWeight={isSameDay(day, new Date()) ? 700 : 400}>
                        {format(day, 'd')}
                      </Typography>
                      {dayBookings.slice(0, 2).map(b => (
                        <Box key={b.id} sx={{
                          bgcolor: statusColors[b.status] || '#3B82F6', color: '#fff',
                          borderRadius: 0.5, px: 0.5, mb: 0.25, fontSize: '0.65rem',
                          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        }}>
                          {b.title}
                        </Box>
                      ))}
                      {dayBookings.length > 2 && (
                        <Typography variant="caption" color="text.secondary">
                          +{dayBookings.length - 2} more
                        </Typography>
                      )}
                    </Grid>
                  );
                });
              })()}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Upcoming Bookings List */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>Bookings</Typography>
        {bookings.filter(b => b.status !== 'cancelled').length === 0 ? (
          <Typography variant="body2" color="text.secondary">No bookings in this period</Typography>
        ) : (
          bookings.filter(b => b.status !== 'cancelled').map(b => (
            <Box key={b.id} sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: 1, borderBottom: '1px solid #f0f0f0',
            }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{b.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(b.startTime), 'MMM d, HH:mm')} - {format(parseISO(b.endTime), 'MMM d, HH:mm')}
                  {' | '}{b.bookedByUserName}
                  {b.taskTitle && <> | Task: {b.taskTitle}</>}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={b.status} size="small"
                  sx={{ bgcolor: statusColors[b.status], color: '#fff', textTransform: 'capitalize' }} />
                <IconButton size="small" color="error" onClick={() => handleDeleteBooking(b.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {/* Create Booking Dialog */}
      <Dialog open={bookingDialog} onClose={() => setBookingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book {resource?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={bookingForm.title}
                placeholder="e.g., Production run, Team meeting"
                onChange={e => setBookingForm({ ...bookingForm, title: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} value={bookingForm.description}
                onChange={e => setBookingForm({ ...bookingForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start" type="datetime-local" value={bookingForm.startTime}
                InputLabelProps={{ shrink: true }}
                onChange={e => setBookingForm({ ...bookingForm, startTime: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End" type="datetime-local" value={bookingForm.endTime}
                InputLabelProps={{ shrink: true }}
                onChange={e => setBookingForm({ ...bookingForm, endTime: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label={`Quantity (${resource?.capacityUnit || 'units'})`}
                type="number" value={bookingForm.quantity}
                onChange={e => setBookingForm({ ...bookingForm, quantity: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={bookingForm.status} label="Status"
                  onChange={e => setBookingForm({ ...bookingForm, status: e.target.value })}>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="tentative">Tentative</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={!bookingForm.title || !bookingForm.startTime || !bookingForm.endTime}
            onClick={handleCreateBooking}>
            Book
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResourceCalendarPage;
